import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
import { useQueryState } from 'nuqs';

import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import WorkflowSubLayout from '@layouts/DashboardLayout/SubLayouts/Workflow';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import FormRequestDetails from '@lib/FormRequestDetails';
import CreditReportDetails from '@lib/CreditReportDetails';
import CustomTermsDetails from '@lib/CustomTermsDetails';
import WorkflowDetails from '@lib/WorkflowDetails';
import DocumentRequestUserDetail from '@lib/DocumentRequestUserDetailV3';
import InvoicePreview from '@lib/InvoicePreview/InvoicePreview';
import { DocumentAccessProvider } from '@providers/DocumentAccess';
import DocumentRequestUserFileDetail from '@lib/DocumentRequestUserFileDetail';
import AIFollowupHistory from '@lib/AIFollowupHistory';
import AIDocumentGenerationForm from '@components/AIDocumentGenerationForm';
import UserWorkflowDocuments from '@lib/UserWorkflowDocuments';
import DocumentPreview from '@lib/DocumentPreview/DocumentPreview';
import HelpCenterIframe from '@lib/HelpCenterIframe';
import UserWorkflowActivityThread from '@/lib/UserWorkflowActivityThread';
import QuestionnaireEditor from '@/components/Questionnaire/questionnaireEditor/QuestionnaireEditor';
import DataReferenceDetail from '@lib/DataReferenceDetail';
import PacerSubmissionPane from '@lib/PacerSubmissionPane';
import PacerPreviewPane from '@/lib/PacerPreviewPane';
import removeNullish from '@/helpers/removeNullish';
import { AnimatePresence, MotionConfig } from 'framer-motion';

import { queryStateParser, queryParam } from './queryStateParser';
import PaneWrapper from './PaneWrapper';

type Pane = {
  key: string;
  content: ReactNode;
};

const Panes: React.FC = () => {
  const { creator: creatorFromDashboard, isInDashboard } = useContext(DashboardContext);
  const { creatorSlug: creatorSlugFromUserProfile, isInUserProfile } = useContext(UserProfileContext);
  const [queryState, setQueryState] = useQueryState(queryParam, queryStateParser);

  let creatorSlug: string | null = null;
  let creatorId: string | null = null;
  let creatorNoodlePhoneNumber: string | null = null;

  if (isInDashboard) {
    creatorSlug = creatorFromDashboard?.slug ?? null;
    creatorId = creatorFromDashboard?.id ?? null;
    creatorNoodlePhoneNumber = creatorFromDashboard?.noodlePhoneNumber ?? null;
  } else if (isInUserProfile) {
    creatorSlug = creatorSlugFromUserProfile;
    creatorId = null;
  }

  const handleRemoveByKey = useCallback((key: string): void => {
    setQueryState(prevState => prevState ? prevState.filter(s => s.key !== key) : null);
  }, []);

  const replaceByTypeAndId = useCallback((type: string, id: string): void => {
    setQueryState(prevState => {
      if(prevState) {
        const localPrevState = [...prevState];
        prevState?.forEach((p, index) => {
          if(p?.type === type) {
            localPrevState[index] = {
              ...p,
              id,
            };
          }
        });
        return localPrevState;

      }
      return null;
    });
  }, []);

  const panes = useMemo<Pane[]>(() => (queryState || [])
    .map(({ type, id, key }) => {
      if (type === 'dataReference') {
        return {
          content: (
            <DataReferenceDetail dataReferenceId={id} />
          ),
          key,
        };
      }

      if (type === 'submissionAttempt') {
        return {
          content: (
            <PacerSubmissionPane submissionAttemptId={id} />
          ),
          key,
        };
      }

      if (type === 'pacerPreview') {
        return {
          content: (
            <DocumentAccessProvider>
              <PacerPreviewPane userWorkflowId={id} />
            </DocumentAccessProvider>
          ),
          key,
        };
      }

      if (type === 'formRequest' && creatorSlug) {
        return {
          content: (
            <MotionConfig reducedMotion='always'>
              <FormRequestDetails creatorSlug={creatorSlug} formRequestId={id} onClose={() => handleRemoveByKey(key)} />
            </MotionConfig>
          ),
          key,
        };
      }

      if (type === 'creditReport') {
        return {
          content: (
            <CreditReportDetails creditReportId={id} />
          ),
          key,
        };
      }

      if (type === 'formRequestTemplate') {
        return {
          content: (
            <QuestionnaireEditor slug={id} />
          ),
          key,
        };
      }

      if (type === 'customTerms') {
        return {
          content: (
            <CustomTermsDetails customTermsId={id} />
          ),
          key,
        };
      }

      if (type === 'documentRequestUserFile') {
        return {
          content: (
            <DocumentRequestUserFileDetail
              documentRequestUserFileId={id}
            />
          ),
          key,
        };
      }

      if (type === 'previewDocument') {
        return {
          content: (
            <DocumentAccessProvider>
              <DocumentPreview documentId={id} />
            </DocumentAccessProvider>
          ),
          key,
        };
      }

      if (type === 'documentRequestUser' && creatorSlug) {
        return {
          content: (
            <DocumentRequestUserDetail
              creatorSlug={creatorSlug}
              documentRequestUserId={id}
            />
          ),
          key,
        };
      }

      if (type === 'invoice' && creatorSlug) {
        return {
          content: (
            <DocumentAccessProvider>
              <InvoicePreview creatorSlug={creatorSlug} invoiceId={id} replaceByTypeAndId={replaceByTypeAndId}/>
            </DocumentAccessProvider>
          ),
          key,
        };
      }

      if (type === 'followupHistory' && creatorId) {
        return {
          content: (
            <AIFollowupHistory
              creatorId={creatorId}
              creatorNoodlePhoneNumber={creatorNoodlePhoneNumber}
              userWorkflowId={id}
            />
          ),
          key,
        };
      }

      if (type === 'aiFollowups' && creatorId) {
        return {
          content: (
            <AIFollowupHistory
              creatorId={creatorId}
              creatorNoodlePhoneNumber={creatorNoodlePhoneNumber}
            />
          ),
          key,
        };
      }

      if (type === 'allCases') {
        return {
          content: (
            <WorkflowSubLayout resourceType='activity' isTableViewOnly hideTabs/>
          ),
          key,
        };
      }

      if (type === 'helpCenter') {
        return {
          content: (
            <HelpCenterIframe />
          ),
          key,
        };
      }

      if (type === 'userWorkflowDocument' && creatorSlug) {
        return {
          content: (
            <AIDocumentGenerationForm
              creatorSlug={creatorSlug}
              userWorkflowDocumentId={id}
              onClose={() => handleRemoveByKey(key)}
            />
          ),
          key,
        };
      }

      if (type === 'userWorkflowDocuments' && creatorId) {
        return {
          content: (
            <DocumentAccessProvider>
              <UserWorkflowDocuments
                creatorId={creatorId}
                isDownloadEnabled
                userWorkflowId={id}
              />
            </DocumentAccessProvider>
          ),
          key,
        };
      }

      if (type === 'workflowTemplate') {
        return {
          content: (
            <WorkflowDetails workflowSlug={id} />
          ),
          key,
        };
      }

      if (type === 'activityThread') {
        return {
          content: (
            <UserWorkflowActivityThread userWorkflowId={id} />
          ),
          key,
        };
      }

      return null;
    })
    .filter(removeNullish),
  [queryState, creatorId, creatorSlug, creatorNoodlePhoneNumber, handleRemoveByKey],
  );

  const handleClose = (): void => {
    setQueryState(prevState => {
      if (!prevState || prevState.length === 0) {
        return null;
      }
      return prevState.slice(0, -1);
    });
  };

  return (
    <AnimatePresence>
      {panes.map(({ key, content }) => (
        <PaneWrapper key={key} onClose={handleClose}>
          {content}
        </PaneWrapper>
      ))}
    </AnimatePresence>
  );
};

export default Panes;
